import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelCreditDeposit,
  createCreditAccountCard,
  createCreditDeposit,
  deleteCreditEntity,
  fetchCreditAccountCards,
  fetchCreditDeposits,
  fetchCreditPending,
  fetchCreditSettings,
  saveCreditEntity,
  updateCreditGrouping,
  updateCreditIssuer,
  type CreatedCreditDeposit,
  type CreditEntity,
} from '../api/credit';
import type {
  CreateCreditAccountCardRequest,
  CreateCreditDepositRequest,
  CreditAccountCard,
  CreditDepositSummary,
  CreditGrouping,
  CreditPayment,
  CreditSettings,
  ListCreditDepositsQuery,
  ListCreditPendingQuery,
  UpdateCreditIssuerRequest,
  UpsertCreditAcquirerRequest,
  UpsertCreditBankRequest,
} from '@deposits/shared';

export function useCreditSettings(locationId: number | null, enabled = true) {
  return useQuery<CreditSettings>({
    queryKey: ['credit-settings', locationId],
    queryFn: () => fetchCreditSettings(locationId!),
    enabled: enabled && locationId != null,
    staleTime: 60_000,
  });
}

/** The location's receipt cards in Arbox — the ledger accounts to pick from. */
export function useCreditAccountCards(locationId: number | null) {
  return useQuery<CreditAccountCard[]>({
    queryKey: ['credit-account-cards', locationId],
    queryFn: () => fetchCreditAccountCards(locationId!),
    enabled: locationId != null,
    staleTime: 5 * 60_000,
    retry: 1,
  });
}

export function useCreateCreditAccountCard(locationId: number | null) {
  const qc = useQueryClient();
  return useMutation<CreditAccountCard, Error, Omit<CreateCreditAccountCardRequest, 'location_id'>>({
    mutationFn: (body) => createCreditAccountCard({ ...body, location_id: locationId! }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['credit-account-cards', locationId] }),
  });
}

/** Every settings write returns the fresh settings; the pending list re-resolves
 *  its companies from them, so refresh it too. */
function useSettingsMutation<V>(locationId: number | null, fn: (v: V) => Promise<CreditSettings>) {
  const qc = useQueryClient();
  return useMutation<CreditSettings, Error, V>({
    mutationFn: fn,
    onSuccess: (data) => {
      qc.setQueryData(['credit-settings', locationId], data);
      qc.invalidateQueries({ queryKey: ['credit-pending'] });
    },
  });
}

export function useUpdateCreditGrouping(locationId: number | null) {
  return useSettingsMutation<CreditGrouping>(locationId, (g) => updateCreditGrouping(locationId!, g));
}

export function useUpdateCreditIssuer(locationId: number | null) {
  return useSettingsMutation<Omit<UpdateCreditIssuerRequest, 'location_id'>>(locationId, (body) =>
    updateCreditIssuer({ ...body, location_id: locationId! }),
  );
}

export function useSaveCreditEntity(locationId: number | null) {
  return useSettingsMutation<{
    entity: CreditEntity;
    id?: number;
    body: Omit<UpsertCreditBankRequest, 'location_id'> | Omit<UpsertCreditAcquirerRequest, 'location_id'>;
  }>(locationId, ({ entity, id, body }) =>
    saveCreditEntity(entity, id, { ...body, location_id: locationId! } as UpsertCreditBankRequest),
  );
}

export function useDeleteCreditEntity(locationId: number | null) {
  return useSettingsMutation<{ entity: CreditEntity; id: number }>(locationId, ({ entity, id }) =>
    deleteCreditEntity(entity, id, locationId!),
  );
}

export function useCreditPending(params: ListCreditPendingQuery | null) {
  return useQuery<CreditPayment[]>({
    queryKey: ['credit-pending', params],
    queryFn: () => fetchCreditPending(params!),
    enabled: params != null,
    staleTime: 30_000,
  });
}

export function useCreditDeposits(params: ListCreditDepositsQuery | null) {
  return useQuery<CreditDepositSummary[]>({
    queryKey: ['credit-deposits', params],
    queryFn: () => fetchCreditDeposits(params!),
    enabled: params != null,
    staleTime: 15_000,
  });
}

export function useCreateCreditDeposit() {
  const qc = useQueryClient();
  return useMutation<CreatedCreditDeposit, Error, CreateCreditDepositRequest>({
    mutationFn: createCreditDeposit,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['credit-pending'] });
      qc.invalidateQueries({ queryKey: ['credit-deposits'] });
    },
  });
}

export function useCancelCreditDeposit() {
  const qc = useQueryClient();
  return useMutation<void, Error, { id: number; reason?: string }>({
    mutationFn: ({ id, reason }) => cancelCreditDeposit(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credit-pending'] });
      qc.invalidateQueries({ queryKey: ['credit-deposits'] });
    },
  });
}
